import React, {useRef, useState, forwardRef, useContext} from "react";
import {
    Button,
    Tooltip,
    Group,
    ActionIcon,
    Box,
    Transition,
} from "@mantine/core";
import IconPencil from "../../../assets/pencil.svg?react";
import classes from "./UserMessage.module.css";
import {useTranslation} from "react-i18next";
import IconX from "../../../assets/x.svg?react";
import IconCheck from "../../../assets/check.svg?react";
import {useAppSelector} from "../../../app/hooks";
import {MessageWithMetadata} from "../../../types";
import {ChatViewContext} from "./ChatView";
import {selectActiveThreadMessages} from "../threadsSlice";
import {readPasteChips} from "../utils/pasteAttachments";
import {PasteChipList} from "../../../components/PasteChip";

interface Props {
    message: MessageWithMetadata;
    initials: string;
    variant?: "default" | "static";
    forceShowEdit?: boolean;
}

const UserMessage = forwardRef<HTMLDivElement, Props>(
    ({message, initials, variant = "default", forceShowEdit = false}, ref) => {
        const {resend} = useContext(ChatViewContext);
        const content = message.content;
        const {t} = useTranslation();
        const [edit, setEdit] = useState<boolean>(false);
        const [value, setValue] = useState<string>(content);
        const textContentInnerRef = useRef<HTMLDivElement>(null);
        const pasteChips = readPasteChips(message.artifacts);
        const isLastMessage = useAppSelector((state) => {
            const msgs = selectActiveThreadMessages(state);
            return msgs.length - 2 === message.position;
        });

        const handleCancel = () => {
            setEdit(false);
            setValue(content);
            if (textContentInnerRef.current) {
                textContentInnerRef.current.innerText = content;
            }
        };

        const handleResend = () => {
            setEdit(false);
            resend(value, message.position);
            if (textContentInnerRef.current) {
                textContentInnerRef.current.innerText = value;
            }
        };

        const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) return;
            const text = selection.toString().replace(/\n+$/, "");
            e.clipboardData.setData("text/plain", text);
            e.preventDefault();
        };

        return (
            <div
                ref={ref}
                className={classes.root}
                data-name="user-message"
                onCopy={handleCopy}
            >
                {pasteChips.length > 0 && (
                    <Box mb="xs">
                        <PasteChipList
                            chips={pasteChips}
                            justify="flex-end"
                            preview
                        />
                    </Box>
                )}
                <Group
                    justify="flex-end"
                    className={classes.group}
                    gap={"sm"}
                    wrap="nowrap"
                    pos="relative"
                >
                    {variant === "default" && isLastMessage && (
                        <>
                            <Transition
                                mounted={!edit}
                                transition="fade-right"
                                duration={200}
                            >
                                {(styles) => (
                                    <Group
                                        justify="end"
                                        gap="xs"
                                        style={styles}
                                        className={classes.initiateEditGroup}
                                    >
                                        <Tooltip
                                            label={t("common.editMessage")}
                                        >
                                            <Button
                                                className={
                                                    forceShowEdit
                                                        ? undefined
                                                        : classes.hidden
                                                }
                                                variant="message-button"
                                                color="gray"
                                                size="xs"
                                                onClick={() => setEdit(true)}
                                            >
                                                <IconPencil height="18px" />
                                            </Button>
                                        </Tooltip>
                                    </Group>
                                )}
                            </Transition>
                            <Transition
                                mounted={edit}
                                transition="fade-left"
                                duration={200}
                            >
                                {(styles) => (
                                    <Group
                                        justify="end"
                                        gap="xs"
                                        style={styles}
                                        className={classes.editGroup}
                                    >
                                        <Tooltip label={t("common.cancel")}>
                                            <ActionIcon
                                                variant="message-button"
                                                color="red"
                                                size="xs"
                                                onClick={handleCancel}
                                            >
                                                <IconX height="18px" />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label={t("common.send")}>
                                            <ActionIcon
                                                variant="message-button"
                                                color={"var(--dib-brand-base)"}
                                                size="xs"
                                                disabled={
                                                    value.trim() === "" ||
                                                    value.trim() ===
                                                        content.trim()
                                                }
                                                onClick={handleResend}
                                            >
                                                <IconCheck height="18px" />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                )}
                            </Transition>
                        </>
                    )}
                    <Box
                        ref={textContentInnerRef}
                        contentEditable={edit ? "plaintext-only" : false}
                        onInput={(e) => setValue(e.currentTarget.innerText)}
                        className={`${classes.content} ${
                            edit ? classes.editing : ""
                        }`}
                    >
                        {content}
                    </Box>
                </Group>
            </div>
        );
    }
);

export default UserMessage;
